<template>
    <loading v-if="loading" :text="$t('find.findingYourTrip')"/>
    <h2 v-else-if="loadingError" class="text-white text-center my-5">{{ loadingError }}</h2>
    <div v-else>
        <!--TODO abstract this to a component. Requires careful coordination with the header in Trip.vue until we do that.        -->
        <div class="d-flex flex-wrap align-items-center mt-4 mx-1">
            <h2 class="text-white flex-grow-1 mb-4 me-2 text-nowrap">{{$t('payment.pageTitle')}}</h2>
            <button
                v-if="this.link.type === 'PAYMENT_IN_FULL' && this.link.audience === 'AGENT' && this.trip.brand.clientEnabled"
                @click="redirectToBrandSummary"
                type="button"
                class="btn btn-sm btn-outline-light my-3 text-nowrap">
                {{$t('payment.backToBookingSummary')}} <font-awesome-icon :icon="['fal', 'up-right-from-square']" />
            </button>
            <button
                v-else
                @click="redirectToTrip"
                type="button"
                class="btn btn-sm btn-outline-light mb-4 text-nowrap">
                {{$t('payment.backToMyTrip')}}
            </button>
        </div>
        <div class="row align-items-stretch">
            <div class="col-xl-8 mb-5 d-flex">
                <trip-summary :trip="trip"/>
            </div>
            <div class="col-xl-4 mb-5 d-flex">
                <managed-trip-user v-if="trip.group || !trip.agent" :user="trip.user"/>
                <travel-advisor v-else :agent="trip.agent"/>
            </div>
        </div>

        <travelers
            v-if="link.audience === 'AGENT' && !trip.group && trip.pnr.travelers"
            :trip="trip"
            :show-pricing="link.type === 'PAYMENT_IN_FULL' && this.trip.pnr.totalPrice !== undefined"
            class="mb-5"
        />

        <bank-transfer-instructions
            v-if="link.type === 'BANK_TRANSFER'"
            :link="link"
            :trip="trip"
        />
        <v-form
            v-else
            @submit="completePayment"
            :initial-values="{formOfPayments:[{}, {}]}"
            v-slot="{errors, meta, submitCount, isSubmitting}"
        >
            <overlay-container
                class="card"
                :active="isSubmitting"
                contentId="CompletePaymentOverlayText"
                :text="$t('payment.completingYourPayment')">
                <div class="card-body">
                    <div v-if="link.type === 'PAYMENT_IN_FULL'" class="text-center lead mb-3">{{$t('payment.reason.PAYMENT_IN_FULL')}}</div>
                    <div v-else class="text-center lead mb-3">{{$t(`payment.reason.${link.reason}`)}}</div>
                    <branded-divider v-if="link.paymentAmount" class="my-4">
                        <div class="d-flex align-items-center">
                            <h3 class="mb-0 text-secondary me-2">{{$filters.beautifyCurrency(link.paymentAmount)}}</h3>
                            <div class="lead"><small>{{$t('payment.paymentDue')}}</small></div>
                        </div>
                    </branded-divider>
                    <accent-box v-if="link.notes" :title="$t('payment.noteFromTravelAdvisor')" class="smaller my-4">
                        <font-awesome-icon class="text-secondary me-1" :icon="['fas', 'quote-left']" />
                        <span class="mx-1" style="white-space: pre-wrap;">{{link.notes}}</span>
                        <font-awesome-icon class="text-secondary ms-1" :icon="['fas', 'quote-right']" />
                    </accent-box>


                    <v-field-array name="formOfPayments" v-slot="{ fields, push, remove }">
                        <!-- TODO too many inline conditions. Abstract -->
                        <div v-if="link.type === 'PAYMENT_IN_FULL' && trip.pnr.travelers && trip.pnr.travelers && trip.pnr.travelers.length > 1 && !trip.pnr.ndc && (this.validatingAirline === undefined || !this.validatingAirline.ticketless)" class="multi-card-select lead mb-3">
                            <div class="form-check">
                                <input type="radio" v-model="isMultiCard" :value="false" id="SingleCard" class="form-check-input">
                                <label class="form-check-label" for="SingleCard">
                                    {{$t('payment.chargeSingleCard')}}
                                </label>
                            </div>
                            <div class="form-check">
                                <input type="radio" v-model="isMultiCard" :value="true" id="MultiCard" class="form-check-input">
                                <label class="form-check-label" for="MultiCard">
                                    {{$t('payment.chargeMultiCard')}}
                                </label>
                            </div>
                        </div>
                        <div v-for="(entry, cardIndex) in fields" :key="entry.key">
                            <accent-box
                                v-if="cardIndex === 0 || isMultiCard"
                                :showBox="isMultiCard"
                                class="mb-4"
                            >
                                <template v-slot:title>
                                    <div class="d-flex align-items-center mb-1">
                                        <h4 class="text-dark flex-grow-1">{{$t('payment.creditCardTitle', {number: cardIndex + 1})}}</h4>
                                        <button
                                            v-if="fields.length > 2"
                                            type="button"
                                            class="btn btn-sm btn-outline-danger"
                                            @click="removeCard(cardIndex, remove);"
                                        >
                                            <font-awesome-icon :icon="['fal', 'circle-minus']"/>
                                            {{$t('payment.remove')}}
                                        </button>
                                    </div>
                                </template>
                                <div v-if="isMultiCard">
                                    <div class="lead mb-1">{{$t('payment.whichTravelers')}}</div>
                                    <div v-if="cardTravelerAssociationsComplete && cardChargeAmounts[cardIndex].price <= 0" class="lead text-danger mb-4">
                                        <font-awesome-icon :icon="['fal', 'circle-exclamation']" class="me-2"/>
                                        <strong>{{$t('payment.errors.allTravelersAssigned')}}</strong>
                                    </div>
                                    <div v-else class="row mb-3">
                                        <div
                                            v-for="(traveler, travelerIndex) in trip.pnr.travelers"
                                            v-show="traveler.cardIndex === undefined || traveler.cardIndex === cardIndex"
                                            class="col-lg-6 lead py-2 card-traveler-association"
                                            :key="traveler.id"
                                        >
                                            <!--TODO smaller text on mobile -->
                                            <div class="form-check">
                                                <input
                                                    type="checkbox"
                                                    v-model="cardTravelerAssociations[cardIndex][travelerIndex]"
                                                    :value="true"
                                                    :id="`Card${cardIndex}Traveler${travelerIndex}Association`"
                                                    class="form-check-input"
                                                    @change="toggleCardTravelerAssociation(cardIndex, travelerIndex, traveler)"
                                                >
                                                <label class="form-check-label text-dark w-100" :for="`Card${cardIndex}Traveler${travelerIndex}Association`">
                                                    <strong class="me-3">{{traveler.fullName}}</strong>
                                                    <small class="text-primary text-nowrap"> + {{$filters.beautifyCurrency(traveler.price)}}</small>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="cardChargeAmounts !== false && cardChargeAmounts[cardIndex].price > 0" class="mb-3">
                                    <branded-divider>
                                        <div class="d-flex align-items-center">
                                            <h3 class="mb-0 text-secondary me-2">{{$filters.beautifyCurrency(cardChargeAmounts[cardIndex].price)}}</h3>
                                            <div class="lead"><small>{{$t('payment.total')}}</small></div>
                                        </div>
                                    </branded-divider>
                                    <div v-if="cardChargeAmounts[cardIndex].feesPrice > 0" class="lead">
                                        <div class="text-center text-dark">
                                            <font-awesome-icon :icon="['fal', 'coins']" class="me-2"/>
                                            <strong>{{$t('payment.multipleChargesWillBeMade')}}</strong>
                                        </div>
                                        <div class="row justify-content-center">
                                            <ul class="col-auto mb-0">
                                                <li>{{$t('payment.chargeForAirfare', {amount: $filters.beautifyCurrency(cardChargeAmounts[cardIndex].airfarePrice)})}}</li>
                                                <li>{{$t('payment.chargeForFees', {amount: $filters.beautifyCurrency(cardChargeAmounts[cardIndex].feesPrice)})}}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-xl-6">
                                        <h4>{{$t('payment.creditCardInformation')}}</h4>
                                        <v-field
                                            :name="`formOfPayments[${cardIndex}].cardNumber`"
                                            :label="$t('validate.fields.creditCardNumber')"
                                            rules="required"
                                            v-slot="{field, errorMessage}"
                                        >
                                            <div class="mb-4">
                                                <div class="input-group input-group-lg d-flex flex-nowrap">
                                                <span class="input-group-text border-end-0" :class="{'border-danger': !!errorMessage}">
                                                    <font-awesome-icon v-if="cardTypes[cardIndex] === undefined || cardTypes[cardIndex] === 'unknown'" class="card-type-icon text-secondary" :icon="['fal', 'credit-card']" />
                                                    <font-awesome-icon v-else class="card-type-icon" :class="`card-type-${cardTypes[cardIndex]}`" :icon="['fab', `cc-${cardTypes[cardIndex]}`]" />
                                                </span>
                                                    <div class="form-floating input-group-lg flex-grow-1">
                                                        <input
                                                            type="text"
                                                            v-bind="field"
                                                            v-mask="cardNumberMasks[cardIndex]"
                                                            v-model="cardNumbers[cardIndex]"
                                                            :id="`CardNumber${cardIndex}`"
                                                            placeholder="Credit Card Number"
                                                            class="form-control rounded-0 rounded-end"
                                                            :class="{'is-invalid': !!errorMessage}"
                                                        />
                                                        <label :for="`CardNumber${cardIndex}`" class="ms-1">
                                                            {{$t('payment.creditCardNumber')}}
                                                        </label>
                                                    </div>
                                                </div>
                                                <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                                            </div>
                                        </v-field>
                                        <v-field
                                            :name="`formOfPayments[${cardIndex}].name`"
                                            :label="$t('validate.fields.nameOnCard')"
                                            rules="required"
                                            v-slot="{field, errorMessage}"
                                        >
                                            <div class="form-floating input-group-lg mb-4">
                                                <input
                                                    type="text"
                                                    v-bind="field"
                                                    :id="`NameOnCard${cardIndex}`"
                                                    placeholder="Name on Card"
                                                    class="form-control"
                                                    :class="{'is-invalid': !!errorMessage}"
                                                />
                                                <label :for="`NameOnCard${cardIndex}`" class="ms-1">
                                                    {{$t('payment.nameOnCard')}}
                                                </label>
                                                <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                                            </div>
                                        </v-field>
                                        <div class="row">
                                            <div class="col-sm mb-4">
                                                <!--TODO improve validation to include year.-->
                                                <v-field
                                                    :name="`formOfPayments[${cardIndex}].cardExpiration`"
                                                    :label="$t('validate.fields.expiration')"
                                                    :rules="{
                                                        required: true,
                                                        regex: /^((0[1-9])|(1[0-2]))\/[0-9]{2}$/
                                                    }"
                                                    v-slot="{field, errorMessage}"
                                                >
                                                    <div class="form-floating input-group-lg">
                                                        <input
                                                            type="text"
                                                            v-bind="field"
                                                            v-mask="'##/##'"
                                                            :id="`CardExpiration${cardIndex}`"
                                                            placeholder="Expiration"
                                                            class="form-control"
                                                            :class="{'is-invalid': !!errorMessage}"
                                                        >
                                                        <label :for="`CardExpiration${cardIndex}`" class="ms-1">
                                                            {{$t('payment.expiration')}} <span>(MM/YY)</span>
                                                        </label>
                                                        <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                                                    </div>
                                                </v-field>
                                            </div>
                                            <div class="col-sm mb-4">
                                                <v-field
                                                    :name="`formOfPayments[${cardIndex}].cardCvvData`"
                                                    :label="$t('validate.fields.cardSecurityCode')"
                                                    rules="required"
                                                    v-slot="{field, errorMessage}"
                                                >
                                                    <div class="form-floating input-group-lg">
                                                        <input
                                                            type="text"
                                                            v-bind="field"
                                                            v-mask="'####'"
                                                            maxlength="4"
                                                            :id="`CVV${cardIndex}`"
                                                            placeholder="CVV"
                                                            class="form-control"
                                                            :class="{'is-invalid': !!errorMessage}"
                                                        >
                                                        <label :for="`CVV${cardIndex}`" class="ms-1">
                                                            {{$t('payment.cvv')}}
                                                        </label>
                                                        <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                                                    </div>
                                                </v-field>
                                            </div>
                                        </div>
                                        <div class="soft-descriptors text-center mb-3">
                                            <div v-if="link.type === 'PAYMENT_IN_FULL' && link.paymentScenario.includes('GDSS')">
                                                {{$t('payment.airlineDeterminesSoftDescriptor')}}
                                                <div v-if="cardChargeAmounts[cardIndex].feesPrice > 0 && link.softDescriptorValue" class="mt-2">
                                                    {{$t('payment.feesSoftDescriptor')}} <strong class="text-nowrap">P4MT.COM {{link.softDescriptorValue}}</strong>
                                                </div>
                                            </div>
                                            <div v-else-if="link.softDescriptorValue && link.paymentScenario.includes('P4MT')" class="mt-2">
                                                {{$t('payment.chargeSoftDescriptor')}} <strong class="text-nowrap">P4MT.COM {{link.softDescriptorValue}}</strong>
                                            </div>

                                        </div>
                                    </div>
                                    <div class="col-xl-6">
                                        <h4>{{$t('payment.billingAddress')}}</h4>
                                        <v-field
                                            :name="`formOfPayments[${cardIndex}].street`"
                                            :label="$t('validate.fields.streetAddress')"
                                            rules="required"
                                            v-slot="{field, errorMessage}"
                                        >
                                            <div class="form-floating input-group-lg mb-4">
                                                <input
                                                    type="text"
                                                    v-bind="field"
                                                    :id="`BillingStreet${cardIndex}`"
                                                    placeholder="BillingStreet"
                                                    :autocomplete="`section-${cardIndex} billing address-line1`"
                                                    class="form-control"
                                                    :class="{'is-invalid': !!errorMessage}"
                                                />
                                                <label :for="`BillingStreet${cardIndex}`" class="ms-1">
                                                    {{$t('payment.streetAddress')}}
                                                </label>
                                                <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                                            </div>
                                        </v-field>
                                        <v-field
                                            :name="`formOfPayments[${cardIndex}].city`"
                                            :label="$t('validate.fields.city')"
                                            rules="required"
                                            v-slot="{field, errorMessage}"
                                        >
                                            <div class="form-floating input-group-lg mb-4">
                                                <input
                                                    type="text"
                                                    v-bind="field"
                                                    :id="`BillingCity${cardIndex}`"
                                                    placeholder="BillingCity"
                                                    :autocomplete="`section-${cardIndex} billing address-level2`"
                                                    class="form-control"
                                                    :class="{'is-invalid': !!errorMessage}"
                                                />
                                                <label :for="`BillingCity${cardIndex}`" class="ms-1">
                                                    {{$t('payment.city')}}
                                                </label>
                                                <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                                            </div>
                                        </v-field>
                                        <div class="row">
                                            <div v-if="countryCodes[cardIndex] === 'US' || countryCodes[cardIndex] === 'CA'" class="col-sm mb-4">
                                                <v-field
                                                    v-if="countryCodes[cardIndex] === 'US'"
                                                    :name="`formOfPayments[${cardIndex}].state`"
                                                    :label="$t('validate.fields.state')"
                                                    :rules="{state_required: countryCodes[cardIndex]}"
                                                    v-slot="{field, errorMessage}"
                                                >
                                                    <state-select
                                                        v-bind="field"
                                                        :class="{'is-invalid': !!errorMessage}"
                                                        :id="`StateSelect${cardIndex}`"
                                                        :autocomplete-attribute="`section-${cardIndex} billing address-level1`"
                                                    />
                                                    <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                                                </v-field>

                                                <v-field
                                                    v-if="countryCodes[cardIndex] === 'CA'"
                                                    :name="`formOfPayments[${cardIndex}].state`"
                                                    :label="$t('validate.fields.province')"
                                                    :rules="{province_required: countryCodes[cardIndex]}"
                                                    v-slot="{field, errorMessage}"
                                                >
                                                    <province-select
                                                        v-bind="field"
                                                        :class="{'is-invalid': !!errorMessage}"
                                                        :id="`ProvinceSelect${cardIndex}`"
                                                        :autocomplete-attribute="`section-${cardIndex} billing address-level1`"
                                                    />
                                                    <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                                                </v-field>
                                            </div>
                                            <div class="col-sm mb-4">
                                                <v-field
                                                    :name="`formOfPayments[${cardIndex}].postalCode`"
                                                    :label="$t('validate.fields.postalCode')"
                                                    rules="required"
                                                    v-slot="{field, errorMessage}"
                                                >
                                                    <div class="form-floating input-group-lg">
                                                        <input
                                                            type="text"
                                                            v-bind="field"
                                                            :id="`BillingPostalCode${cardIndex}`"
                                                            placeholder="BillingPostalCode"
                                                            :autocomplete="`section-${cardIndex} billing postal-code`"
                                                            class="form-control"
                                                            :class="{'is-invalid': !!errorMessage}"
                                                        />
                                                        <label :for="`BillingPostalCode${cardIndex}`" class="ms-1">
                                                            {{$t('payment.postalCode')}}
                                                        </label>
                                                        <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                                                    </div>
                                                </v-field>
                                            </div>
                                        </div>
                                        <v-field
                                            :name="`formOfPayments[${cardIndex}].countryCode`"
                                            :label="$t('validate.fields.country')"
                                            rules="required"
                                            v-slot="{field, errorMessage}"
                                        >
                                            <div class="mb-4">
                                                <country-select
                                                    v-bind="field"
                                                    v-model="countryCodes[cardIndex]"
                                                    :class="{'is-invalid': !!errorMessage}"
                                                    :id="`CountrySelect${cardIndex}`"
                                                    :autocomplete-attribute="`section-${cardIndex} billing country`"
                                                />
                                                <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                                            </div>
                                        </v-field>
                                    </div>
                                    <div class="col-xl-6">
                                        <h4>{{$t('payment.cardholderContactInformation')}}</h4>
                                        <v-field
                                            :name="`formOfPayments[${cardIndex}].email`"
                                            :label="$t('validate.fields.emailAddress')"
                                            rules="required"
                                            v-slot="{field, errorMessage}"
                                        >
                                            <div class="form-floating input-group-lg mb-4">
                                                <input
                                                    type="text"
                                                    v-bind="field"
                                                    :id="`BillingEmail${cardIndex}`"
                                                    placeholder="BillingEmail"
                                                    :autocomplete="`section-${cardIndex} billing email`"
                                                    class="form-control"
                                                    :class="{'is-invalid': !!errorMessage}"
                                                />
                                                <label :for="`BillingEmail${cardIndex}`" class="ms-1">
                                                    {{$t('payment.emailAddress')}}
                                                </label>
                                                <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                                            </div>
                                        </v-field>
                                        <v-field
                                            :name="`formOfPayments[${cardIndex}].phone`"
                                            :label="$t('validate.fields.phoneNumber')"
                                            :rules="{
                                                required: true,
                                                valid_vue_tel_input: phoneNumberInputs[cardIndex]
                                            }"
                                            :validateOnChange="false"
                                            :validateOnModelUpdate="false"
                                            v-slot="{field, errorMessage}"
                                        >
                                            <div class="form-floating input-group-lg mb-4" @click="focusElement(`#BillingPhone${cardIndex} input`)">
                                                <vue-tel-input
                                                    v-bind="field"
                                                    v-model="phoneNumbers[cardIndex]"
                                                    @on-input="phoneNumberInput"
                                                    mode="national"
                                                    default-country="US"
                                                    :id="`BillingPhone${cardIndex}`"
                                                    :dropdownOptions="{
                                                        showDialCodeInSelection: true,
                                                        showSearchBox: true,
                                                        showFlags: true,
                                                        showDialCodeInList: true
                                                    }"
                                                    :inputOptions="{
                                                        styleClasses: `form-control vue-tel-input-${cardIndex}`,
                                                        autocomplete: `section-${cardIndex} billing tel`
                                                    }"
                                                    class="form-control"
                                                    :class="{'is-invalid': !!errorMessage}"
                                                />
                                                <label :for="`BillingPhone${cardIndex}`" class="ms-1">
                                                    {{$t('payment.phoneNumber')}}
                                                </label>
                                                <div class="text-danger my-1" v-if="errorMessage">{{errorMessage}}</div>
                                            </div>
                                        </v-field>
                                        <div class="small text-center">{{$t('payment.contactInformationCollectionNote')}}</div>
                                    </div>
                                </div>
                            </accent-box>
                        </div>
                        <button
                            v-if="isMultiCard && fields.length < trip.pnr.travelers.length"
                            type="button"
                            class="btn btn-lg btn-outline-secondary"
                            @click="pushCard(push)"
                        >
                            <font-awesome-icon :icon="['fal', 'circle-plus']"/>
                            {{$t('payment.addAnotherCard')}}
                        </button>
                    </v-field-array>
                    <branded-divider v-if="link.type !== 'PAYMENT_IN_FULL' && link.paymentAmount" class="my-4">
                        <div class="d-flex align-items-center">
                            <h3 class="mb-0 text-secondary me-2">{{$filters.beautifyCurrency(link.paymentAmount)}}</h3>
                            <div class="lead"><small>{{$t('payment.paymentDue')}}</small></div>
                        </div>
                    </branded-divider>
                    <branded-divider v-else class="my-4"/><!-- no content in slot -->
                    <v-field
                        name="paymentTermsAgreed"
                        :label="$t('validate.fields.creditCardPaymentAgreement')"
                        rules="required"
                        type="checkbox"
                        value="1"
                        v-slot="{field, errorMessage}"
                    >
                        <div class="mb-4">
                            <div class="d-flex justify-content-center lead mb-4">
                                <div class="form-check">
                                    <input
                                        type="checkbox"
                                        v-bind="field"
                                        value="1"
                                        id="PaymentTermsAgreed"
                                        class="form-check-input"
                                    >

                                    <label class="form-check-label" for="PaymentTermsAgreed">
                                        <!-- Weird translating here, but getting the HTML into the translation string was really clunky. -->
                                        {{$t('payment.paymentTermsStart')}}
                                        <strong>
                                            <a :href="$config.urls.terms" target="_blank" rel="noopener noreferrer">
                                                {{$t('payment.creditCardPaymentAgreement')}}
                                                <font-awesome-icon :icon="['fal', 'up-right-from-square']" />
                                            </a>
                                        </strong>
                                        {{$t('payment.paymentTermsEnd')}}
                                    </label>
                                </div>
                            </div>
                            <div class="text-danger text-center my-1" v-if="errorMessage">{{errorMessage}}</div>
                        </div>
                    </v-field>

                    <div id="PaymentSubmitErrors">
                        <div v-if="errors.submit" class="lead text-center text-danger mb-4">
                            <strong>
                                <font-awesome-icon class="me-1" :icon="['fal', 'circle-exclamation']" />
                                {{ errors.submit }}
                            </strong>
                            <div v-for="(paymentError, paymentErrorIndex) in paymentErrors" :key="paymentErrorIndex">
                                {{paymentError}}
                            </div>
                        </div>
                        <div v-else-if="!meta.valid && submitCount > 0" class="lead text-center text-danger mb-4">
                            <strong>
                                <font-awesome-icon class="me-1" :icon="['fal', 'circle-exclamation']" /> {{$t('payment.errors.invalid')}}
                            </strong>
                        </div>
                    </div>
                    <div class="row g-0 justify-content-center">
                        <button type="submit" class="btn btn-lg btn-primary col-sm-11 col-md-8 col-lg-6 col-xl-5" :disabled="isSubmitting">{{$t('payment.completePayment')}}</button>
                    </div>
                </div>
            </overlay-container>
        </v-form>
    </div>
</template>

<script>
//External components
// import { reactive } from 'vue';
import { Form as VForm, Field as VField, FieldArray as VFieldArray} from 'vee-validate';
import { VueTelInput } from 'vue-tel-input';

//P4MT Components
import ManagedTripUser from "@/components/ManagedTripUser";
import TravelAdvisor from '@/components/TravelAdvisor';
import Travelers from '@/components/Travelers';
import TripSummary from '@/components/TripSummary';
import AccentBox from "@/components/common/AccentBox";
import BrandedDivider from "@/components/common/BrandedDivider";
import Loading from '@/components/common/Loading';
import OverlayContainer from '@/components/common/OverlayContainer';
import CountrySelect from '@/components/form/CountrySelect';
import StateSelect from '@/components/form/StateSelect';
import ProvinceSelect from '@/components/form/ProvinceSelect';
import BankTransferInstructions from '@/components/payment/BankTransferInstructions';
import {completePayment, getTripAndLink} from '@/api';

//TODO move elsewhere?

export default {
    name: 'Payment',
    components: {
        BankTransferInstructions,
        VForm,
        VField,
        VFieldArray,
        VueTelInput,
        ManagedTripUser,
        TravelAdvisor,
        Travelers,
        TripSummary,
        AccentBox,
        BrandedDivider,
        Loading,
        OverlayContainer,
        CountrySelect,
        StateSelect,
        ProvinceSelect
    },
    data() {
        return {
            isMultiCard: false,
            cardNumbers: [],
            cardTravelerAssociations: [[],[]],
            countryCodes: ['US','US'],
            paymentTermsAgreed: false,
            loading: true,
            loadingError: false,
            paymentErrors: [],

            //These fields are used for facilitating the collection of phone numbers from the vue-tel-input component as there were compatibility difficulties with vee-validate and vue-tel-input.
            phoneNumbers: [],
            phoneNumberInputs: []
        }
    },
    computed: {
        trip() {
            return this.$store.state.trip;
        },
        link() {
            return this.$store.state.link;
        },
        cardTypes() {
            let cardTypes = [];

            this.cardNumbers.forEach((cardNumber, index) => {
                if (!cardNumber) cardTypes[index] = 'unknown';
                else if (cardNumber.match(/^4/) != null) cardTypes[index] = 'visa';
                else if (cardNumber.match(/^(34|37)/) != null) cardTypes[index] = 'amex';
                else if (cardNumber.match(/^5[1-5]/) != null) cardTypes[index] = 'mastercard';
                else if (cardNumber.match(/^(65|6011)/) != null) cardTypes[index] = 'discover';
                else cardTypes[index] = 'unknown';
            });

            return cardTypes;
        },
        cardNumberMasks() {
            let cardNumberMasks = [];

            this.cardTypes.forEach((cardType, index) => {
               cardNumberMasks[index] = cardType === 'amex' ? '#### ###### #####' : '#### #### #### ####';
            });

            return cardNumberMasks;
        },
        cardChargeAmounts() {

            if (this.link.type == 'PAYMENT_IN_FULL' && this.trip.pnr) {
                let cardChargeAmounts = [];
                if (this.isMultiCard) {
                    for (let index = 0; index < this.cardTravelerAssociations.length; index++) {
                        cardChargeAmounts.push({
                            price: 0,
                            airfarePrice: 0,
                            feesPrice: 0
                        });
                    }

                    this.trip.pnr.travelers.forEach(traveler => {
                        if (traveler.cardIndex !== undefined) {
                            cardChargeAmounts[traveler.cardIndex].price += traveler.price;

                            if (traveler.airfarePrice) {
                                cardChargeAmounts[traveler.cardIndex].airfarePrice += traveler.airfarePrice;
                            }

                            if (traveler.feesPrice) {
                                cardChargeAmounts[traveler.cardIndex].feesPrice += traveler.feesPrice;
                            }
                        }
                    });
                } else {
                    return [
                        {
                            price: this.trip.pnr.totalPrice,
                            airfarePrice: this.trip.pnr.totalAirfarePrice ?? 0,
                            feesPrice: this.trip.pnr.totalFeesPrice ?? 0
                        }
                    ]
                }
                return cardChargeAmounts;
            } else {
                return false;
            }
        },
        cardTravelerAssociationsComplete() {
            //Return true if all travelers have been associated to a card, or this is not a multipay scenario.
            //false if any associations are missing.

            //Start out assuming it is complete, and look for conditions that would make it false.
            let isComplete = true;

            if (this.isMultiCard) {
                this.trip.pnr.travelers.forEach(traveler => {
                    //If a traveler is missing a card index return false.
                    if (traveler.cardIndex === undefined) {
                        isComplete = false;
                    }
                });
            }

            //return the calculated value.
            return isComplete;
        },
        tripType() {
            return this.trip.group || !this.trip.pnr ? 'GROUP' : 'INDIVIDUAL';
        },
        validatingAirline() {
            //This is an Individual trip field only.
            if(this.tripType !== 'INDIVIDUAL') return undefined;

            return Object.values(this.trip.pnr.airlines).find(airline => airline.iata === this.trip.pnr.validatingAirlineCode);
        },
    },
    methods: {
        completePayment(values, actions) {
            //Clear out previous payment submission errors.
            this.paymentErrors = [];

            //If we've made it this far, then front end validation with vee-validate was successful. Prepare our API payload and perform additional verification.
            let formOfPayments;
            if(this.isMultiCard) {
                formOfPayments = values.formOfPayments;

                //In multi card scenarios, each traveler must be assigned to at least one card.
                if (!this.cardTravelerAssociationsComplete) {
                    actions.setErrors({
                        submit: this.$t('payment.errors.travelersNotAssigned')
                    });
                    return;
                }

                //Validate that each card must have at least one traveler.
                let allCardsHaveTravelers = true;
                this.cardTravelerAssociations.forEach(cardTravelerAssociation => {
                    let hasTravelerAssociation = false;
                    cardTravelerAssociation.forEach(isAssociated => {
                        if (isAssociated) {
                            hasTravelerAssociation = true;
                        }
                    });

                    if (!hasTravelerAssociation) {
                        allCardsHaveTravelers = false;
                    }
                });
                if (allCardsHaveTravelers === false) {
                    actions.setErrors({
                        submit: this.$t('payment.errors.creditCardNotAssigned')
                    });
                    return;
                }
            } else {
                //If we're not in a multi card scenario, simply take the first form of payment.
                formOfPayments = [
                    values.formOfPayments[0]
                ];
            }


            formOfPayments.forEach((formOfPayment, index) => {
                //remove whitespace from CC number.
                formOfPayment.cardIdentifier = index + 1;
                formOfPayment.cardNumber = formOfPayment.cardNumber.replace(/\s+/g, '');

                //Add formatted number
                if (this.phoneNumberInputs[index] && this.phoneNumberInputs[index].number) {
                    formOfPayment.phone = this.phoneNumberInputs[index].number;
                }

                //split cardExpiration into cardExpirationMonth & cardExpirationYear
                [formOfPayment.cardExpirationMonth, formOfPayment.cardExpirationYear] = formOfPayment.cardExpiration.split('/'); //Fun assignment trick: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Destructuring_assignment

                formOfPayment.pnrTravelerTokens = [];
            });

            //Populate traveler tokens if this is multicard.
            if(this.isMultiCard) {
                this.trip.pnr.travelers.forEach(traveler => {
                    formOfPayments[traveler.cardIndex].pnrTravelerTokens.push(traveler.token);
                });
            }

            //Scroll page to middle of loading animation.
            document.getElementById('CompletePaymentOverlayText').scrollIntoView({behavior: 'smooth', block: 'center'});

            return completePayment(this.$route.params.tripId, this.$route.params.linkId, this.$route.params.linkTinyHash, formOfPayments)
                .then(response => {
                    if (response.data.accepted) {

                        //TODO send e-commerce transaction to google analytics if this was a CHARGE_CREDIT_CARD

                        //Send e-commerce transaction to google analytics, if this was a PAYMENT_IN_FULL link.
                        if (this.link.type === 'PAYMENT_IN_FULL') {
                            try {

                                let gtagPurchaseParams = {
                                    transaction_id: this.trip.pnr.recordLocator,
                                    affiliation: this.trip.agent.companyNameOverride ?? this.trip.agent.company.name,
                                    value: this.trip.pnr.totalPrice,
                                    currency: 'USD',
                                    tax: 0,
                                    shipping: 0,
                                    items: []
                                };

                                //If the brand includes a tracking ID, add a "send_to" so this goes to both the default P4MT property and the brand's
                                if (this.trip.brand && this.trip.brand.googleAnalyticsTrackingId) {
                                    gtagPurchaseParams.send_to = [
                                        'default',
                                        this.trip.brand.googleAnalyticsTrackingId
                                    ];
                                }

                                this.trip.pnr.travelers.forEach(traveler => {
                                    gtagPurchaseParams.items.push({
                                        id: this.trip.pnr.validatingAirlineCode + '_' + this.trip.pnr.fareTypeCode,
                                        name: this.validatingAirline.name + ' ' + this.$t(`common.fareType.${this.trip.pnr.fareTypeCode}`),
                                        brand: this.trip.pnr.validatingAirlineCode,
                                        category: this.trip.pnr.fareTypeCode,
                                        quantity: 1,
                                        price: traveler.price
                                    });
                                });

                                this.$gtag.event('purchase', gtagPurchaseParams);

                            } catch (exception) {
                                console.log(exception);
                            }
                        }

                        if (this.link.type === 'PAYMENT_IN_FULL' && this.link.audience === 'AGENT' && this.trip.brand.clientEnabled) {
                            //Redirect offsite if this was a payment in full enterred by the agent on a client enabled site.
                            this.redirectToBrandSummaryWithReason('received');
                        } else if (this.link.reason === 'EMERGENCY_SUPPORT_INCIDENTALS' && this.link.audience === 'AGENT' && this.trip.brand.clientEnabled) {
                            //Redirect offsite if this was a form of payment for the purpose of covering emergency support incidentals by the agent on a client enabled site.
                            this.redirectToBrandEmergencySupport();
                        } else {
                            //Redirect to the Trip view with the an action completed.
                            this.$store.commit('setActionCompleted', 'PAYMENT_COMPLETED');
                            this.$router.push({
                                name: 'trip',
                                params: {
                                    tripId: this.trip.tripId,
                                    tripTinyHash: this.trip.tinyHash
                                }
                            });
                        }
                    }
                })
                .catch(error => {
                    actions.setErrors({
                        submit: this.$t('payment.errors.completePayment.default')
                    });
                    if(error.response) {
                        if(error.response.data.canAttemptAgain === false) {
                            this.$store.commit('setActionCompleted', 'PAYMENT_BLOCKED');
                            this.$router.push({
                                name: 'trip',
                                params: {
                                    tripId: this.trip.tripId,
                                    tripTinyHash: this.trip.tinyHash
                                }
                            });
                        } else if (error.response.data.errors && error.response.data.errors.length > 0) {
                            this.paymentErrors = error.response.data.errors;

                            if (error.response.status === 400) {
                                actions.setErrors({
                                    submit: this.$t('payment.errors.completePayment.paymentIssue')
                                });
                                this.paymentErrors.push(this.$t('payment.errors.completePayment.reviewCardholderBillingInformation'));
                            }
                        }
                    }

                    //Scroll page to errors.
                    document.getElementById('PaymentSubmitErrors').scrollIntoView({behavior: 'smooth', block: 'center'});
                });
        },
        pushCard(fieldArrayPush) {
            this.cardTravelerAssociations.push([]);
            this.countryCodes.push('US');
            fieldArrayPush({});
        },
        removeCard(cardIndex, fieldArrayRemove) {

            //Temporarily clear traveler associations
            this.trip.pnr.travelers.forEach(traveler => {
                traveler.cardIndex = undefined;
            });

            this.cardTravelerAssociations.splice(cardIndex, 1);

            //Rebuild traveler Associations
            this.cardTravelerAssociations.forEach((cardTravelerAssociation, cardIndex) => {
               cardTravelerAssociation.forEach((isAssociated, travelerIndex) => {
                   if (isAssociated) {
                       this.trip.pnr.travelers[travelerIndex].cardIndex = cardIndex;
                   }
               })
            });

            this.countryCodes.splice(cardIndex, 1);
            fieldArrayRemove(cardIndex);
        },
        toggleCardTravelerAssociation(cardIndex, travelerIndex, traveler) {
            if (this.cardTravelerAssociations[cardIndex][travelerIndex]) {
                traveler.cardIndex = cardIndex;
                this.cardTravelerAssociations.forEach((cardTravelerAssociation, index) => {
                    if (cardIndex !== index) {
                        this.cardTravelerAssociations[index][travelerIndex] = false;
                    }
                });
            } else {
                traveler.cardIndex = undefined;
            }
        },
        redirectToTrip() {
            this.$router.push({
                name: 'trip',
                params: {
                    tripId: this.trip.tripId,
                    tripTinyHash: this.trip.tinyHash
                }
            });
        },
        redirectToBrandEmergencySupport() {
            this.loading = true; //Prevent the user from double submitting the page during the redirect.
            window.location.href = 'http://' + this.trip.brand.domain + `/contact/emergency/${this.trip.tripId}/${this.trip.hash}/received`;
        },
        redirectToBrandSummaryWithReason(reasonCode) {
            this.loading = true; //Prevent the user from double submitting the page during the redirect.
            window.location.href = 'http://' + this.trip.brand.domain + `/summary/${this.trip.tripId}/${reasonCode}`;
        },
        //Helper since optional arguments for the function above were giving me trouble.
        redirectToBrandSummary() {
            return this.redirectToBrandSummaryWithReason('');
        },
        phoneNumberInput(number, phoneObject, element) {

            //#hack Utilize a style class to bind the phone object from this event to a
            element.classList.forEach((className) => {
                if (className.substring(0, className.length - 1) === 'vue-tel-input-') {
                    this.phoneNumberInputs[className.substring(className.length - 1)] = phoneObject;
                }
            });

        },
        focusElement(query) {
            document.querySelector(query).focus();
        }
    },
    mounted() {
        //Clear trip & link from store if they do not match the route params.
        if(this.trip !== null && this.trip.tripId != this.$route.params.tripId) {
            this.$store.commit('setTrip', null);
        }
        if(this.link !== null && (this.link.id != this.$route.params.linkId || this.link.tinyHash != this.$route.params.linkTinyHash)) {
            this.$store.commit('setLink', null);
        }

        //If we're missing trip or link, retrieve from API.
        if(this.trip === null || this.link === null) {
            getTripAndLink(this.$route.params.tripId, this.$route.params.linkId, this.$route.params.linkTinyHash)
                .then(response => {
                    this.$store.commit('setTrip', response.data.trip);
                    this.$store.commit('setLink', response.data.link);
                    // reactive(response.data.trip.pnr.travelers);
                })
                .catch(error => {
                    //By default we will show the "default" error.
                    this.loadingError = this.$t('payment.errors.getTripAndLink.default');

                    //Look in the error response for "known" errors that we want to pass onto the user.
                    if(error.response && error.response.data.errors) {
                        let knownErrors = ['linkCompleted', 'linkExpired'];
                        knownErrors.forEach(knownError => {
                            if(error.response.data.errors[knownError]) {
                                this.loadingError = this.$t(`payment.errors.getTripAndLink.${knownError}`);
                            }
                        })
                    }

                    //TODO redirect to nicer looking error page.
                })
                .finally(() => {
                    this.loading = false;
                });
        } else {
            //If we already have this trip & link in state, we don't need to retrieve anything, so simply stop loading.
            this.loading = false;
        }
    }
}
</script>

<style lang="scss" scoped>
    //TODO this might be better handled with a global rule for labels & checkbox/radio inputs.
    .card-traveler-association,
    .multi-card-select {
        label,
        input {
            cursor: pointer;
        }

    }
    .card-type-icon {
        font-size: 1.75rem;
    }
    .card-type-visa {
        color: $visaColor;
    }
    .card-type-mastercard {
        color: $mastercardColor;
    }
    .card-type-discover {
        color: $discoverColor;
    }
    .card-type-amex {
        color: $amexColor;
    }

    //These labels have some Capital letters that run together.
    //Add a bit of letter spacing to create breathing room.
    #CVV + label,
    #CardExpiration + label span {
        letter-spacing: 1px;
    }
</style>